import { Component, OnChanges, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { CreatePage } from 'src/app/missing-info/create/create.page';
import { DetailsPage } from 'src/app/missing-info/details/details.page';
import { MissingInfoTypes } from 'src/app/missing-info/MissingInfoTypes';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { DetailsPage as EmailDetails } from '../../../secure-email/details/details.page';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import { DetailsComponent as TeamMemberDetailsPage } from '../../team-members/details/details.component';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';

@Component({
  selector: 'app-missing-info-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class MissingInfoTableComponent implements OnInit, OnChanges {

  @Input() url_mod = '';
  @Input() hide_add_button: boolean = false;
  @Input() hide_view_buttons: boolean = false;
  @Input() hideColumns: string[] = [];
  @Input() project: any = null;
  @Input() task: any = null;
  @Input() date_selector: boolean = true;
  @Input() in_dates = false;
  @Input() client_completed = false;

  // Only show items for this contact / company, plus items with no contact or company assigned (used by the
  // select modals so something can't be linked across clients)
  @Input() restrict_client_guid = '';
  @Input() restrict_company_guid = '';

  private MatchesRestrictedClient(item: any): boolean {
    if (!this.restrict_client_guid && !this.restrict_company_guid) return true;
    if (!item.client_guid && !item.company_guid) return true;
    if (this.restrict_client_guid) return item.client_guid === this.restrict_client_guid;
    return item.company_guid === this.restrict_company_guid;
  }

  // ── Selection mode (used by the "select" modals, e.g. linking a time entry to an item) ──
  @Input() select_mode = false;
  @Input() max_allowed_selections = 1;
  @Input() selected_items: any[] = [];
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  IsSelected(item: any): boolean {
    return this.selected_items.some(i => i.guid === item.guid);
  }

  ToggleSelected(item: any) {
    if (!this.select_mode) return;
    if (this.IsSelected(item)) {
      this.selected_items = this.selected_items.filter(i => i.guid !== item.guid);
    } else if (this.selected_items.length < this.max_allowed_selections) {
      this.selected_items = [...this.selected_items, item];
    } else if (this.max_allowed_selections === 1) {
      // With a max of one, picking another item swaps the selection
      this.selected_items = [item];
    } else {
      return;
    }
    this.selectedItemsChange.emit(this.selected_items);
  }

  items: any[] = [];
  all_items: any[] = [];
  team_members: any[] = [];
  all_clients: any[] = [];
  all_companies: any[] = [];
  clients_roles: any[] = [];
  @Input() show_all_missing_info: 'all' | 'uncompleted' | 'completed' = 'uncompleted';
  loaded_items: boolean = false;
  missing_info_view: string = 'all';
  selected_team_member: string = '';
  @Input() current_start_date: Date;
  @Input() current_end_date: Date;
  dateValue: Date[];
  pendingStartDate: string = '';
  pendingEndDate: string = '';
  readonly maxDate = dayjs().format('YYYY-MM-DD');
  private initialized = false;

  availableColumns: ColumnConfig[] = [
    { key: 'created_date', label: 'Date Created', visible: true },
    { key: 'name', label: 'Name', visible: true },
    { key: 'missing_info_type', label: 'Missing Info Type', visible: true },
    { key: 'client_entity_name', label: 'Client Assigned To', visible: true },
    { key: 'client_completed', label: 'Client Completed', visible: true },
    { key: 'client_completed_date', label: 'Client Completed Date', visible: true },
    { key: 'project_title', label: 'Project Assigned To', visible: true },
    { key: 'recorded_by_name', label: 'Item Recorded By', visible: true },
    { key: 'intended_recipient_name', label: 'Intended Recipient', visible: true },
    { key: 'completed', label: 'Completed', visible: true },
    { key: 'completed_date', label: 'Completed Date', visible: true },
    { key: 'completed_by', label: 'Completed By', visible: true },
    { key: 'is_private', label: 'Private', visible: true },
  ];

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService,
    private alertController: AlertController
  ) {}

  ngOnInit() {
    this.dateValue = new Array(2);
    this.syncColumnsFromHideColumns();
    this.initialized = true;
    this.GetData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized) return;

    if (changes.url_mod || changes.current_start_date || changes.current_end_date || changes.client_completed || changes.show_all_missing_info) {
      this.GetData();
    }
  }

  isColumnVisible(key: string): boolean {
    const col = this.availableColumns.find(c => c.key === key);
    return col ? col.visible && !this.hideColumns.includes(key) : !this.hideColumns.includes(key);
  }

  syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => {
      col.visible = !this.hideColumns.includes(col.key);
    });
  }

  syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
  }

  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal secondary-modal tall-modal',
      componentProps: { columns: columnsCopy },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.syncHideColumnsFromColumns();
      }
    });
    return await modal.present();
  }

  DateChanged(value) {
    const newStart = value[0];
    const newEnd = value[1];
    if (
      (!this.current_start_date || !this.current_end_date) ||
      (newStart && newEnd && (
        this.current_start_date.getTime() !== newStart.getTime() ||
        this.current_end_date.getTime() !== newEnd.getTime()
      ))
    ) {
      this.current_start_date = newStart;
      this.current_end_date = newEnd;
      this.dateValue[0] = this.current_start_date;
      this.dateValue[1] = this.current_end_date;

      if (this.loaded_items) this.GetData();
    }
  }

  FormatDates() {
    if (!this.current_start_date) this.current_start_date = dayjs().startOf('week').startOf('day').toDate();
    if (!this.current_end_date) this.current_end_date = dayjs().endOf('week').endOf('day').toDate();
    this.dateValue[0] = this.current_start_date;
    this.dateValue[1] = this.current_end_date;
    this.pendingStartDate = dayjs(this.current_start_date).format('YYYY-MM-DD');
    this.pendingEndDate = dayjs(this.current_end_date).format('YYYY-MM-DD');
  }

  get canApplyDates(): boolean {
    if (!this.pendingStartDate || !this.pendingEndDate) return false;
    if (!this.current_start_date || !this.current_end_date) return true;
    return (
      dayjs(this.pendingStartDate).format('YYYY-MM-DD') !== dayjs(this.current_start_date).format('YYYY-MM-DD') ||
      dayjs(this.pendingEndDate).format('YYYY-MM-DD') !== dayjs(this.current_end_date).format('YYYY-MM-DD')
    );
  }

  applyDateFilter() {
    if (!this.canApplyDates) return;
    const start = dayjs(this.pendingStartDate).startOf('day').toDate();
    const end = dayjs(this.pendingEndDate).endOf('day').toDate();
    this.DateChanged([start, end]);
  }

  getTableDescription(): string {
    const completedLabel = this.client_completed ? 'Completed By Client' : this.show_all_missing_info;

    let ownerPart = '';
    if (this.missing_info_view === 'mine') {
      ownerPart = 'my ';
    } else if (this.missing_info_view === 'others' && this.selected_team_member) {
      const member = this.team_members.find(m => m.guid === this.selected_team_member);
      if (member) ownerPart = `${member.display_name || member.name}'s `;
    }

    let datePart = '';
    if ((this.date_selector || this.in_dates) && this.current_start_date && this.current_end_date) {
      const start = dayjs(this.current_start_date).format('MMM D, YYYY');
      const end = dayjs(this.current_end_date).format('MMM D, YYYY');
      datePart = ` from ${start} to ${end}`;
    }

    let quickSearchPart = '';
    if (this.quickSearchTerm?.trim()) {
      quickSearchPart = `, quick searching for: ${this.quickSearchTerm.trim()}`;
    }

    return `Showing ${ownerPart}${completedLabel} missing info items${datePart}${quickSearchPart}.`;
  }

  async GetData() {
    this.loaded_items = false;

    let url = 'missing_info?';
    if (this.client_completed) url += 'client_completed=1';
    else if (this.show_all_missing_info === 'uncompleted') url += 'completed=0';
    else if (this.show_all_missing_info === 'completed') url += 'completed=1';

    if ((this.date_selector || this.in_dates)) {
      this.FormatDates();
      let start_date = this.current_start_date.toISOString();
      let end_date = this.current_end_date.toISOString();
      url += '&start_date=' + start_date + '&end_date=' + end_date;
    }

    url += this.url_mod;

    await Promise.all([
      this.GetTeamMembers(),
      this.GetAllClients(),
      this.GetAllCompanies(),
      this.GetClientsRoles()
    ]);

    const data: any = await this.apiService.get(url);
    if (data.success && data.data) this.all_items = data.data;
    else this.all_items = [];

    await this.FormatData();
    this.items = this.GetFilteredItems(this.all_items);
    this.loaded_items = true;
  }

  async GetAllClients() {
    const data: any = await this.apiService.GetClients();
    if (data?.data) this.all_clients = data.data;
  }

  async GetAllCompanies() {
    const data: any = await this.apiService.GetCompanies();
    if (data?.data) this.all_companies = data.data;
  }

  async GetClientsRoles() {
    const data: any = await this.apiService.get('firms/clients/roles');
    if (data?.success) this.clients_roles = data.data || [];
  }

  GetFilteredItems(items: any[]): any[] {
    if (!items) return [];
    items = items.filter(i => this.MatchesRestrictedClient(i));
    let filtered = items;
    if (this.missing_info_view === 'mine') {
      const current_user = this.globalService.GetCurrentUser();
      filtered = current_user ? items.filter(i => i.team_member_guid === current_user.guid) : items;
    } else if (this.missing_info_view === 'others') {
      filtered = this.selected_team_member ? items.filter(i => i.team_member_guid === this.selected_team_member) : [];
    }
    return filtered.filter(i => this.MatchesQuickSearch(i));
  }

  OnViewChange() {
    this.items = this.GetFilteredItems(this.all_items);
  }

  // Quick search (mirrors components/projects/table's quick search) - searches title, client
  // assigned to, project assigned to, and who recorded the item. Routed through GetFilteredItems, the
  // single place every view (all/mine/others) already filters through, so this applies everywhere
  // without needing to be threaded into each call site separately. Debounced 300ms via
  // quickSearchDebounceTimer so filtering doesn't recompute on every keystroke - quickSearchInput
  // tracks the raw typed value for instant display, quickSearchPending drives the "Searching..."
  // indicator while quickSearchTerm (the actually-applied value) is still catching up.
  quickSearchTerm = '';
  quickSearchInput = '';
  quickSearchPending = false;
  private quickSearchDebounceTimer: any;

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term;
    this.quickSearchPending = true;
    clearTimeout(this.quickSearchDebounceTimer);
    this.quickSearchDebounceTimer = setTimeout(() => {
      this.quickSearchTerm = term;
      this.quickSearchPending = false;
      this.items = this.GetFilteredItems(this.all_items);
    }, 300);
  }

  private MatchesQuickSearch(item: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    const values = [item?.title, item?.client_entity_name, item?.project_title, item?.recorded_by_name];
    return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
  }

  OnTeamMemberChange() {
    this.items = this.GetFilteredItems(this.all_items);
  }

  // Saved filter presets (see FilterPresetsComponent / components/projects/table's
  // GetCurrentTableFilters for the pattern this mirrors). This table has no PrimeNG column filters, so
  // there's nothing to bundle beyond the "above the table" toggle buttons themselves (All/Uncompleted/
  // Completed, All/Mine/Other Team Member, and the selected team member). Arrow functions so they stay
  // bound to this component when handed to app-filter-presets as plain function references.
  GetCurrentTableFilters = (): any => {
    return {
      columnFilters: null,
      toolbar: {
        show_all_missing_info: this.show_all_missing_info,
        missing_info_view: this.missing_info_view,
        selected_team_member: this.selected_team_member,
      }
    };
  };

  ApplyTableFilters(saved: any) {
    const toolbar = saved?.toolbar ?? saved;
    if (!toolbar) return;
    if (toolbar.show_all_missing_info) this.show_all_missing_info = toolbar.show_all_missing_info;
    if (toolbar.missing_info_view) this.missing_info_view = toolbar.missing_info_view;
    if (toolbar.selected_team_member !== undefined) this.selected_team_member = toolbar.selected_team_member;
    this.GetData();
  }

  DescribeToolbarState = (toolbar: any): string[] => {
    const statusLabels: { [key: string]: string } = { all: 'All Missing Info', uncompleted: 'Uncompleted Missing Info', completed: 'Completed Missing Info' };
    const parts: string[] = [`Status: ${statusLabels[toolbar.show_all_missing_info] || toolbar.show_all_missing_info}`];

    const viewLabels: { [key: string]: string } = { all: 'All Missing Info', mine: 'My Missing Info', others: 'Other Team Member' };
    parts.push(`View: ${viewLabels[toolbar.missing_info_view] || toolbar.missing_info_view}`);
    if (toolbar.missing_info_view === 'others' && toolbar.selected_team_member) {
      const name = this.team_members?.find(m => m.guid === toolbar.selected_team_member)?.display_name || 'a team member';
      parts.push(`Team Member: ${name}`);
    }
    return parts;
  };

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data;
  }

  async FormatData(items: any[] = this.all_items) {
    if (!items) return;

    for (const item of items) {
      // Resolve missing_info_type text from type number - 12 is the current Question Request
      // value (matches Emails.EmailTypes.question_request); 5 is the retired legacy value the
      // backend still normalizes old records to 12 from on save, but existing rows that haven't
      // been re-saved yet may still carry 5, so both map to the same label here.
      if (item.type == 12 || item.type == MissingInfoTypes.outstanding_question) item.missing_info_type = 'Question';
      else if (item.type == MissingInfoTypes.missing_document) item.missing_info_type = 'Missing Documents';
      else item.missing_info_type = 'None';

      // Resolve client / company name from cache
      item.client_entity_role_name = '';
      if (item.client_guid) {
        const client = this.all_clients.find(c => c.guid === item.client_guid);
        item.client_entity_name = client ? client.display_name : '';
      } else if (item.company_guid) {
        const company = this.all_companies.find(c => c.guid === item.company_guid);
        item.client_entity_name = company ? company.name : '';
        if (item.role_guid) {
          const role = this.clients_roles.find(r => r.guid === item.role_guid);
          item.client_entity_role_name = role ? role.title : '';
        }
      }

      // Resolve project title
      if (item.project_guid) {
        let projectData: any = this.project;
        if (this.project == null) projectData = await this.apiService.get(`projects/${item.project_guid}`);
        if (projectData.success && projectData.data && projectData.data[0]) {
          item.project_title = projectData.data[0].title;
        }
      }

      // Resolve team member names from guids using the cached team_members list
      if (item.creator_guid) {
        const creator = this.team_members.find(tm => tm.guid === item.creator_guid);
        item.recorded_by_name = creator ? creator.display_name : '';
      }

      if (item.team_member_guid) {
        const member = this.team_members.find(tm => tm.guid === item.team_member_guid);
        item.intended_recipient_name = member ? member.display_name : '';
      }

      // Not completed: no completed date or completed by (rows are updated in place, so values from earlier have to be cleared)
      if (!item.completed) {
        item.completed_date = '';
        item.completed_by = '';
        item.completed_by_guid = '';
      }
      else if (item.completed_by_guid) {
        const completedBy = this.team_members.find(tm => tm.guid === item.completed_by_guid);
        item.completed_by = completedBy ? completedBy.display_name : '';

        if (!item.completed_by) {
          const client = this.all_clients.find(c => c.guid === item.completed_by_guid);
          item.completed_by = client ? client.display_name : '';
        }
      }
    }
  }

  async OpenCreateModal(item: any = null) {
    let project_guid = '';
    let client_entity_guid = '';
    let client_entity_type = '';
    let task_guid = '';

    if (this.project?.guid) {
      // Locked into the project passed in from the project details page - the project already
      // knows its own client/company, so pin the missing info to both.
      project_guid = this.project.guid;
      if (this.project.client_guid) { client_entity_guid = this.project.client_guid; client_entity_type = 'client'; }
      else if (this.project.company_guid) { client_entity_guid = this.project.company_guid; client_entity_type = 'company'; }
    } else if (this.url_mod) {
      const clientMatch = this.url_mod.match(/client=([^&'"]+)/);
      const companyMatch = this.url_mod.match(/company=([^&'"]+)/);
      const projectMatch = this.url_mod.match(/project=([^&'"]+)/);
      if (clientMatch) { client_entity_guid = clientMatch[1]; client_entity_type = 'client'; }
      else if (companyMatch) { client_entity_guid = companyMatch[1]; client_entity_type = 'company'; }
      if (projectMatch) project_guid = projectMatch[1];
    }

    // Opened from a task's details page - lock the task in too.
    if (this.task?.guid) task_guid = this.task.guid;

    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      componentProps: {
        missing_info_item: item ? { ...item } : null,
        prefill_entity_guid: client_entity_guid,
        prefill_entity_type: client_entity_type,
        prefill_project_guid: project_guid,
        prefill_task_guid: task_guid
      },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((result: any) => {
      if (!result.data) return;
      // A save hands back the saved item; otherwise the item opened is the one that changed
      const saved = Array.isArray(result.data) ? result.data[0] : result.data;
      const guid = saved?.guid || item?.guid;
      if (guid) this.RefreshOneItem(guid);
      else this.GetData();
    });
    await modal.present();
  }

  // Whether an item belongs in the table for its current filters - the same rules the list request applies on the server
  private MatchesCurrentFilters(item: any): boolean {
    if (this.client_completed) { if (!item.client_completed) return false; }
    else if (this.show_all_missing_info === 'uncompleted') { if (item.completed) return false; }
    else if (this.show_all_missing_info === 'completed') { if (!item.completed) return false; }

    if ((this.date_selector || this.in_dates) && this.current_start_date && this.current_end_date) {
      const created = dayjs(item.created_date);
      if (created.isBefore(dayjs(this.current_start_date)) || created.isAfter(dayjs(this.current_end_date))) return false;
    }

    const wanted = (name: string) => this.url_mod?.match(new RegExp(name + '=([^&\'"]+)'))?.[1];
    const checks: [string, string][] = [
      ['client', 'client_guid'], ['company', 'company_guid'], ['project', 'project_guid'],
      ['task', 'task_guid'], ['team_member', 'team_member_guid']
    ];
    for (const [param, field] of checks) {
      const value = wanted(param);
      if (value && item[field] !== value) return false;
    }
    return true;
  }

  // After an item was created or changed: reloads just that one and updates the table - the row is replaced, added if it now
  // belongs here, or dropped if it no longer does - instead of reloading the whole list (and the lookups with it)
  async RefreshOneItem(guid: string) {
    try {
      const data: any = await this.apiService.get(`missing_info/${guid}`);
      const fresh = data?.success ? data.data?.[0] : null;
      if (!fresh) throw new Error('missing info not found');
      const existing = this.all_items.find(i => i.guid === guid);
      if (this.MatchesCurrentFilters(fresh)) {
        // Updated in place when it was already a row, so anything the row holds is kept
        const row = existing ? Object.assign(existing, fresh) : fresh;
        await this.FormatData([row]);
        if (!existing) this.all_items = [...this.all_items, row];
      } else {
        this.all_items = this.all_items.filter(i => i.guid !== guid);
      }
      this.items = this.GetFilteredItems(this.all_items);
    } catch (e) {
      // Couldn't update just that row - fall back to reloading the list
      this.GetData();
    }
  }

  // A deleted item leaves the table without reloading it
  private RemoveItemRow(guid: string) {
    this.all_items = this.all_items.filter(i => i.guid !== guid);
    this.items = this.GetFilteredItems(this.all_items);
  }

  // Opens the client/company details modal for whichever entity type the item is assigned to.
  // Looks the full object up from the already-cached all_clients/all_companies lists (populated by
  // GetAllClients/GetAllCompanies in GetData) rather than an extra API call - matches the pattern
  // used by companies/details/details.page.ts's PresentClientDetails, which also takes the full
  // client/company object rather than just a guid.
  async OpenClientEntityDetails(item: any) {
    if (item.client_guid) {
      const client = this.all_clients.find(c => c.guid === item.client_guid);
      if (!client) return;
      const modal = await this.modalController.create({
        component: ClientDetailsPage,
        cssClass: 'large-modal',
        componentProps: { client },
        backdropDismiss: false
      });
      await modal.present();
    } else if (item.company_guid) {
      const company = this.all_companies.find(c => c.guid === item.company_guid);
      if (!company) return;
      const modal = await this.modalController.create({
        component: CompanyDetailsPage,
        cssClass: 'large-modal',
        componentProps: { company },
        backdropDismiss: false
      });
      await modal.present();
    }
  }

  // Mirrors OpenProjectDetails in missing-info/details/details.page.ts.
  async OpenProjectDetails(item: any) {
    if (!item.project_guid) return;
    const modal = await this.modalController.create({
      component: ProjectDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.project_guid },
      backdropDismiss: false
    });
    await modal.present();
  }

  // "Item Recorded By" is always a team member (creator_guid) - mirrors
  // components/secure-email/table's OpenSentByDetails, which opens the same DetailsComponent.
  async OpenRecordedByDetails(item: any) {
    if (!item.creator_guid) return;
    const modal = await this.modalController.create({
      component: TeamMemberDetailsPage,
      cssClass: 'large-modal',
      componentProps: { team_member: { guid: item.creator_guid } },
      backdropDismiss: false
    });
    await modal.present();
  }

  async OpenDetailsModal(item: any) {
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.guid },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((result: any) => {
      if (result.data?.deleted) this.RemoveItemRow(item.guid);
      else if (result.data?.refresh) this.RefreshOneItem(item.guid);
    });
    await modal.present();
  }

  async ToggleComplete(item: any) {
    if (item.completed) {
      // pause_option 1 = a task is paused, 2 = the whole project is paused - either one gets
      // resumed automatically once this item is marked complete, so confirm that's expected.
      if (item.project_guid && item.pause_option) {
        const target = item.pause_option == 2 ? 'project' : 'task';
        const targetName = target === 'project' ? item.project_title : '';
        const confirmed = await this.ConfirmUnpauseOnComplete(target, targetName);
        if (!confirmed) {
          item.completed = false;
          return;
        }
      }

      item.completed_date = new Date().toISOString();
      const current_user = this.globalService.GetCurrentUser();
      item.completed_by = current_user ? current_user.display_name : '';
    } else {
      item.completed_date = '';
      item.completed_by = '';
    }
    const data: any = await this.apiService.put(`missing_info/${item.guid}`, item);
    this.globalService.presentToastMessage(data.message, data.success);
    if (!data.success) {
      item.completed = !item.completed;
      item.completed_date = '';
      item.completed_by = '';
    } else this.RefreshOneItem(item.guid);
  }

  async ConfirmUnpauseOnComplete(target: 'project' | 'task', targetName: string): Promise<boolean> {
    return new Promise(async (resolve) => {
      const label = targetName ? `the ${target} "${targetName}"` : `the ${target}`;
      const alert = await this.alertController.create({
        header: 'Resume ' + (target === 'project' ? 'Project' : 'Task'),
        message: `Marking this as completed will unpause ${label}. Continue?`,
        buttons: [
          { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
          { text: 'Confirm', handler: () => resolve(true) }
        ]
      });
      await alert.present();
    });
  }


  async presentEmailDetailsPage(guid: string) {
    const modal = await this.modalController.create({
      component: EmailDetails,
      cssClass: 'large-modal',
      componentProps: { guid: guid },
      backdropDismiss: false
    });
    modal.onDidDismiss().then(() => {
      
    });
    return await modal.present();
  }


  async StartTimerForItem(item: any) {
    const project = item;
    if (this.globalService.timer_project?.guid === project.guid) {
      this.globalService.StartTimer();
      return;
    }
    const timerActive = this.globalService.timer_wall_start || this.globalService.total_seconds > 0;
    if (timerActive) {
      const alert = await this.alertController.create({
        header: 'Timer Already Running',
        message: 'A timer is already running. Restart it for "' + project.title + '"?',
        buttons: [
          { text: 'Keep Current', role: 'cancel' },
          {
            text: 'Restart',
            handler: () => {
              this.globalService.ResetTimer();
              this.globalService.timer_project = project;
              this.globalService.timer_project_type = 'missing information';
              this.globalService.StartTimer();
              this.globalService.presentToastMessage('Timer started for: ' + project.title, true);
            }
          }
        ]
      });
      return await alert.present();
    }
    this.globalService.timer_project = project;
    this.globalService.timer_project_type = 'missing information';
    this.globalService.StartTimer();
    this.globalService.presentToastMessage('Timer started for: ' + project.title, true);
  }

  async ConfirmDiscardTimer(project: any) {
    const alert = await this.alertController.create({
      header: 'Discard Timer?',
      message: `Are you sure you want to discard the timer for "${project.title}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Discard', role: 'destructive', handler: () => this.globalService.ResetTimer() }
      ]
    });
    await alert.present();
  }

  async StopAndLogItem(item: any) {
    this.globalService.StopTimer();
    const now = new Date();
    const start = this.globalService.GetTimerStart(now);
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { initial_start_time: start, initial_end_time: now, initial_worked_ms: this.globalService.total_seconds, reset_timer_on_save: true, missing_info: item }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.success) this.globalService.ResetTimer();
    });
    return await modal.present();
  }

  async OpenManualTimeEntryForItem(item: any) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { missing_info: item }
    });
    return await modal.present();
  }
}
